// Self-healing sweep for the pre-order line-property contract: a cart line
// that reached /cart/add without _preorder/_preorder_batch/_preorder_eta
// (third-party cart bridges pushing off-page products, sessions on
// stale-cached HTML, future paths) is listed by
// lettoria-preorder-cart-repair.liquid — rendered with the cart in
// cart-products, so every drawer morph / bundled section swap refreshes it —
// and patched here through /cart/change.js before the order is placed.
// Shopify cannot amend properties after checkout, so a missed line loses its
// batch attribution forever; this sweep is the last line of defence behind
// the per-path builders in lettoria-preorder-property-builder.js.
//
// /cart/change.js REPLACES the line's properties wholesale — the payload is
// the line's existing properties (whatever another layer attached) merged
// with the armed trio. A successful change gives the line a NEW key, so a
// stale manifest entry simply fails on re-attempt; the attempt cap absorbs
// that and the next cart render drops it from the manifest. No UI dispatch is
// needed: the drawer's promise line already falls back to the live metafield
// for display, and the server cart is what checkout reads.
import { StandardEvents } from '@shopify/events';

const attemptsByLineKey = new Map();
let sweepInFlight = false;

// Theme add paths morph the drawer section, so the DOM manifest is fresh.
// The CommSlayer bridge refreshes no theme section on Horizon (its cart
// selectors match nothing here) — for its `cart:updated` signal the manifest
// must be fetched against the current server cart instead.
async function readManifest(fetchFresh) {
  if (fetchFresh) {
    try {
      const response = await fetch('/?section_id=lettoria-preorder-repair-manifest', { headers: { Accept: 'text/html' } });
      if (!response.ok) return null;
      const html = await response.text();
      const match = html.match(/<script[^>]*data-lettoria-preorder-repair[^>]*>([\s\S]*?)<\/script>/);
      return match ? match[1] : null;
    } catch {
      return null;
    }
  }
  return document.querySelector('script[data-lettoria-preorder-repair]')?.textContent ?? null;
}

async function sweep(fetchFresh = false) {
  if (sweepInFlight) return;
  sweepInFlight = true;
  try {
    const raw = await readManifest(fetchFresh);
    if (!raw) return;
    let entries;
    try {
      entries = JSON.parse(raw);
    } catch {
      return;
    }
    for (const entry of entries) {
      if (!entry?.key || !entry?.armed?.code) continue;
      const tried = attemptsByLineKey.get(entry.key) ?? 0;
      if (tried >= 2) continue;
      attemptsByLineKey.set(entry.key, tried + 1);
      const properties = { ...entry.existing, _preorder: 'true', _preorder_batch: entry.armed.code };
      if (entry.armed.eta) properties._preorder_eta = entry.armed.eta;
      try {
        await fetch(`${window.Theme?.routes?.cart_change_url ?? '/cart/change'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: entry.key, quantity: entry.quantity, properties }),
        });
      } catch {
        // Network failure: the attempt cap leaves one retry for a later trigger.
      }
    }
  } finally {
    sweepInFlight = false;
  }
}

// A leaked line can already sit in the server-rendered cart (page load after
// a stale-cache add) …
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => sweep(), { once: true });
} else {
  sweep();
}

// … or appear through any theme add/update. Wait for the update promise,
// then a macrotask, so the morphed manifest is in the DOM before reading it.
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const settled = event.promise ?? Promise.resolve();
  settled.then(() => setTimeout(sweep, 0)).catch(() => {});
});

// … or through the CommSlayer bridge, which announces with this custom event
// but refreshes no theme section on Horizon — fetch the manifest fresh.
document.addEventListener('cart:updated', () => setTimeout(() => sweep(true), 0));
